/**
 * "Send files" (ADR-023 A23): the owner picks pictures or sounds in the host's
 * file dialog and they are copied into the forge's `public/`, where Claude
 * can use them and they ship with the arche. A stopped forge receives them at
 * its next start. Files marked proprietary are never offered (`forArche`), and
 * the server refuses anything `public/` cannot ship.
 */
import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Loader2, Upload } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { OpenFileDialog } from '@/components/files/file-dialog/OpenFileDialog';
import { getSelectedVaultHolderId } from '@/components/HolderSwitcher';
import type { VaultFileDialogDocumentDto } from '@/lib/api';
import type { ForgeDeliverySummary } from '../types';

/** Mirrors the server's FORGE_DELIVERY_MAX_FILES. */
export const FORGE_SEND_MAX_FILES = 20;

/** What `public/` ships; the server decides from the bytes, this only narrows the dialog. */
const SENDABLE = {
  kinds: ['image', 'audio'] as const,
  mimeTypes: ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'audio/ogg'],
};

const STATUS_CLASSES: Record<ForgeDeliverySummary['status'], string> = {
  pending: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
  dispatched: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
  applied: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
  failed: 'bg-red-500/10 text-red-600 border-red-500/20',
};

function statusLabel(delivery: ForgeDeliverySummary, isRunning: boolean): string {
  switch (delivery.status) {
    case 'pending':
      return isRunning ? 'Sending' : 'Waiting for the forge to start';
    case 'dispatched':
      return 'Sending';
    case 'applied':
      return 'In public/';
    case 'failed':
      return 'Not sent';
  }
}

interface ForgeSendFilesProps {
  deliveries: ForgeDeliverySummary[];
  onSend: (body: { holderId: string; documentIds: string[] }) => Promise<unknown>;
  isSending: boolean;
  isRunning: boolean;
}

export function ForgeSendFiles({ deliveries, onSend, isSending, isRunning }: ForgeSendFilesProps) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = async (files: VaultFileDialogDocumentDto[]) => {
    const holderId = files[0]?.holderId;
    if (!holderId) return;
    setError(null);
    try {
      await onSend({ holderId, documentIds: files.map((f) => f.documentId) });
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The files could not be sent.');
      setOpen(false);
    }
  };

  return (
    <div className="rounded-lg border p-3 space-y-3" data-testid="forge-send-files">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium">From your Vault</h3>
        <Button size="sm" variant="outline" onClick={() => setOpen(true)} disabled={isSending}>
          {isSending ? (
            <Loader2 className="h-4 w-4 animate-spin mr-1" />
          ) : (
            <Upload className="h-4 w-4 mr-1" />
          )}
          Send files
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Send pictures and sounds from your Vault into the arche&apos;s public/ folder, for Claude to
        use. PNG, JPEG, WebP, GIF or OGG, up to 4 MB each.
      </p>
      {error && (
        <p className="text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
      {deliveries.length > 0 && (
        <ul className="space-y-1.5 max-h-40 overflow-y-auto">
          {deliveries.slice(0, 6).map((d) => (
            <li key={d.id} className="text-xs space-y-0.5">
              <span className="flex items-center gap-2 min-w-0">
                <Badge variant="outline" className={STATUS_CLASSES[d.status]}>
                  {statusLabel(d, isRunning)}
                </Badge>
                <span className="text-muted-foreground truncate">
                  {d.createdAt
                    ? formatDistanceToNow(new Date(d.createdAt), { addSuffix: true })
                    : ''}
                </span>
              </span>
              <span
                className="block truncate text-muted-foreground"
                title={(d.appliedPaths ?? d.files.map((f) => f.name)).join(', ')}
              >
                {(d.appliedPaths ?? d.files.map((f) => f.name)).join(', ')}
              </span>
              {d.status === 'failed' && d.failureMessage && (
                <span className="block text-destructive truncate" title={d.failureMessage}>
                  {d.failureMessage}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      <OpenFileDialog
        open={open}
        onOpenChange={setOpen}
        multiSelect
        selectionLimit={FORGE_SEND_MAX_FILES}
        accept={SENDABLE}
        // Never offers proprietary files: what enters a forge can leave it.
        forArche
        allowUpload={false}
        initialHolderId={getSelectedVaultHolderId()}
        memoryKey="forge-send-files"
        title="Send files to this forge"
        description={`Pick up to ${FORGE_SEND_MAX_FILES} pictures or sounds. They are copied into the arche's public/ folder, where Claude can use them, and ship with the arche.`}
        confirmLabel="Send"
        onConfirm={confirm}
      />
    </div>
  );
}
